import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import FeedbackPin from './FeedbackPin';

import styles from './styles.module.scss';

const CATEGORY_MAPPING = {
  error: { class: 'error' },
  suggestion: { class: 'warning' },
  positive: { class: 'positive' },
  'deeper-insights': { class: 'deeper-insights' },
};

// Severity hierarchy for priority determination (higher number = higher priority)
const SEVERITY_PRIORITY = {
  'deeper-insights': 4,
  error: 3,
  suggestion: 2,
  positive: 1,
};

const MAX_VISIBLE_DOTS = 5;

const ImageCarousel = ({
  feedback,
  images,
  onBackClick,
  selectedImageIndex = 0,
  onImageIndexChange,
  isLoading = false,
}) => {
  const [dotStartIndex, setDotStartIndex] = useState(0); // For pagination
  const [currentImageAspectRatio, setCurrentImageAspectRatio] = useState(null);

  // Calculate active page indices based on feedback (pages that have feedback)
  const feedbackPageIndices = useMemo(() => {
    if (!feedback || !Array.isArray(feedback)) {
      return [];
    }

    // Filter out dismissed feedback first
    const activeFeedback = feedback.filter(
      item => !item.dismiss && item.dismiss !== true
    );

    // Extract unique page numbers from non-dismissed feedback and convert to indices (page - 1)
    const uniquePages = [...new Set(activeFeedback.map(item => item.page))];
    const indices = uniquePages
      .map(page => page - 1) // Convert page number to array index
      .filter(index => index >= 0 && index < images.length) // Ensure indices are valid
      .sort((a, b) => a - b); // Sort indices in ascending order

    return indices;
  }, [feedback, images.length]);

  // Calculate highest severity for each page
  const pageSeverityMap = useMemo(() => {
    if (!feedback || !Array.isArray(feedback)) {
      return new Map();
    }

    const getCategory = item => {
      if (item.isDeeperInsight) {
        return 'deeper-insights';
      }

      return item.category || '';
    };

    const severityMap = new Map();

    // Filter out dismissed feedback
    const activeFeedback = feedback.filter(
      item => !item.dismiss && item.dismiss !== true
    );

    activeFeedback.forEach(item => {
      const pageIndex = item.page - 1; // Convert to 0-based index
      const category = getCategory(item);
      const currentPriority = SEVERITY_PRIORITY[category] || 0;

      if (!severityMap.has(pageIndex) || severityMap.get(pageIndex).priority < currentPriority) {
        severityMap.set(pageIndex, {
          category,
          priority: currentPriority,
          severityClass: category && CATEGORY_MAPPING[category]
            ? CATEGORY_MAPPING[category].class
            : '',
        });
      }
    });

    return severityMap;
  }, [feedback]);

  // Calculate visible dots for pagination (max 5 at a time)
  const visibleDots = useMemo(() => {
    const totalImages = images.length;

    if (totalImages <= MAX_VISIBLE_DOTS) {
      // eslint-disable-next-line no-unused-vars
      return images.map((_, index) => index);
    }

    // Calculate the range of dots to show
    let start = dotStartIndex;
    const end = Math.min(start + MAX_VISIBLE_DOTS, totalImages);

    // Adjust if we're near the end
    if (end === totalImages && totalImages - start < MAX_VISIBLE_DOTS) {
      start = Math.max(0, totalImages - MAX_VISIBLE_DOTS);
    }

    // eslint-disable-next-line no-unused-vars
    return Array.from({ length: end - start }, (_, i) => start + i);
  }, [images.length, dotStartIndex]);

  // Update dot pagination when selected image changes
  useEffect(() => {
    const totalImages = images.length;

    if (totalImages <= MAX_VISIBLE_DOTS) {
      setDotStartIndex(0);

      return;
    }

    // Keep selected image in visible range
    const currentStart = dotStartIndex;
    const currentEnd = Math.min(currentStart + MAX_VISIBLE_DOTS, totalImages);

    if (selectedImageIndex < currentStart) {
      // Selected image is before visible range, move start back
      setDotStartIndex(Math.max(0, selectedImageIndex));
    }
    else if (selectedImageIndex >= currentEnd) {
      // Selected image is after visible range, move start forward
      setDotStartIndex(Math.max(0, selectedImageIndex - MAX_VISIBLE_DOTS + 1));
    }
    else if (selectedImageIndex === currentStart + MAX_VISIBLE_DOTS - 1 && currentEnd < totalImages) {
      // When user reaches the last visible dot (5th position) and there are more images,
      // shift the window to show the next dot and hide the first one
      setDotStartIndex(currentStart + 1);
    }
    else if (selectedImageIndex === currentStart && currentStart > 0) {
      // When user moves back to the second visible dot (2nd position) and there are previous images,
      // shift the window backward to show the previous dot and hide the last one
      setDotStartIndex(currentStart - 1);
    }
  }, [selectedImageIndex, images.length, dotStartIndex]);

  // Reset aspect ratio when image changes to avoid flash of incorrect styling
  useEffect(() => {
    setCurrentImageAspectRatio(null);
  }, [selectedImageIndex]);

  // Get feedback items for current page with pin locations, handling dismissed findings and overlapping positions
  const currentPageFeedback = useMemo(() => {
    if (!feedback || !Array.isArray(feedback) || !images[selectedImageIndex]) {
      return [];
    }

    const currentPage = images[selectedImageIndex].page || selectedImageIndex + 1;

    // Filter for current page, exclude dismissed findings, and only include items with pin locations
    const activeFeedback = feedback.filter(
      item => item.page === currentPage
      && !item.dismiss
      && item.dismiss !== true
      && item.pin_location
    );

    // Handle overlapping pin positions
    const positionTracker = new Map();

    return activeFeedback.map(feedbackItem => {
      const pinLocation = feedbackItem.pin_location;

      const positionKey = `${pinLocation.x_percent}-${pinLocation.y_percent}`;

      // Find the original index of this feedback item in the full feedback array
      const originalIndex = feedback.findIndex(item => item === feedbackItem);
      const feedbackNumber = originalIndex + 1; // Convert to 1-based numbering

      // Determine category and severity class (matching FeedbackBox logic)
      const getCategory = item => {
        if (item.isDeeperInsight) {
          return 'deeper-insights';
        }

        return item.category || '';
      };

      const category = getCategory(feedbackItem);
      const severityClass = category && CATEGORY_MAPPING[category]
        ? CATEGORY_MAPPING[category].class
        : '';

      if (positionTracker.has(positionKey)) {
        // If position is already used, offset the new pin to the right
        const offset = positionTracker.get(positionKey) * 8; // 5% offset for each overlapping pin

        positionTracker.set(positionKey, positionTracker.get(positionKey) + 1);

        return {
          ...feedbackItem,
          feedbackNumber,
          severityClass,
          adjustedLocation: {
            x_percent: Math.min(pinLocation.x_percent + offset, 95), // Don't go beyond 95% to avoid edge cutoff
            y_percent: pinLocation.y_percent,
          },
        };
      }
      positionTracker.set(positionKey, 1);

      return {
        ...feedbackItem,
        feedbackNumber,
        severityClass,
        adjustedLocation: pinLocation,
      };
    });
  }, [feedback, selectedImageIndex, images]);

  const handlePrevious = () => {
    if (selectedImageIndex > 0) {
      handleImageSelect(selectedImageIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedImageIndex < images.length - 1) {
      handleImageSelect(selectedImageIndex + 1);
    }
  };

  const handleDotClick = index => {
    // Allow clicking on any page index
    handleImageSelect(index);
  };

  if (isLoading) {
    return (
      <div className={cn(styles['detail-image-wrapper'])}>
        <div className={styles['detail-image-placeholder']}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #E2E8F0',
                borderTop: '2px solid #3B82F6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            Loading images...
          </div>
        </div>
      </div>
    );
  }

  if (!images || images.length === 0) {
    return (
      <div className={cn(styles['detail-image-wrapper'])}>
        <div className={styles['detail-image-placeholder']}>
          No images available
        </div>
      </div>
    );
  }

  const currentImage = images[selectedImageIndex];

  return (
    <>
      <div className={styles['detail-preview-header']}>
        <button
          onClick={onBackClick}
          className={styles['detail-back-button']}
        >
          ←
        </button>
        {currentImage?.page && (
          <span className={styles['detail-page-indicator']}>
            {currentImage.page}
            {' '}
            of
            {' '}
            {images.length}
          </span>
        )}
      </div>
      <div className={cn('flex flex-1 flex-col gap-4 overflow-auto')}>
        {/* Main Image Display */}
        <div className={styles['detail-image-wrapper']}>
          <div className={cn(styles['inner'], { [styles['full-height']]: currentImageAspectRatio < 1 })}>
            <img
              key={selectedImageIndex}
              src={currentImage.url}
              alt={currentImage.alt || `Design ${selectedImageIndex + 1}`}
              className={styles['detail-image']}
              loading={selectedImageIndex <= 1 ? 'eager' : 'lazy'}
              onLoad={e => {
                // Simple way to get original image dimensions
                const { naturalWidth, naturalHeight } = e.target;
                const aspectRatio = naturalWidth / naturalHeight;

                // Update aspect ratio state to conditionally apply class
                setCurrentImageAspectRatio(aspectRatio);
              }}
            />
            {/* Feedback Pins */}
            {currentPageFeedback.map((feedbackItem, index) => (
              <FeedbackPin
                key={`${feedbackItem.id || index}-pin`}
                number={feedbackItem.feedbackNumber}
                x={feedbackItem.adjustedLocation.x_percent}
                y={feedbackItem.adjustedLocation.y_percent}
                severity={feedbackItem.severityClass}
              />
            ))}
          </div>
        </div>

        {/* Navigation Controls */}
        <div className={styles['detail-carousel']}>
          <button
            onClick={handlePrevious}
            disabled={selectedImageIndex === 0}
            className={cn(
              styles['detail-carousel-button'],
              {
                [styles['detail-carousel-button--disabled']]: selectedImageIndex === 0,
              }
            )}
            aria-label="Previous image"
          >
            ‹
          </button>

          {/* Dots Indicator */}
          <div className={styles['detail-dots']}>
            {visibleDots.map(index => {
              const hasFeedback = feedbackPageIndices.includes(index);
              const isSelected = index === selectedImageIndex;
              const pageSeverity = pageSeverityMap.get(index);
              const severityClass = pageSeverity?.severityClass || '';

              return (
                <div
                  key={index}
                  className={styles['step-container']}
                  onClick={() => handleDotClick(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleDotClick(index);
                    }
                  }}
                  aria-label={`Go to image ${index + 1}`}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className={cn(styles['dot-circle'], {
                      // Use severity-based colors only when selected and has feedback
                      [styles[`dot-circle--${severityClass}`]]: isSelected && hasFeedback && severityClass,
                      [styles['dot-circle--selected-with-feedback']]: isSelected && hasFeedback && !severityClass,
                      [styles['dot-circle--selected-no-feedback']]: isSelected && !hasFeedback,
                      [styles['dot-circle--unselected']]: !isSelected,
                    })}
                  />
                  {hasFeedback && (
                    <div
                      className={cn(styles['feedback-ring'], {
                        // Use severity-based ring colors for active state
                        [styles[`feedback-ring--active-${severityClass}`]]: isSelected && severityClass,
                        [styles['feedback-ring--active']]: isSelected && !severityClass,
                        [styles['feedback-ring--inactive']]: !isSelected,
                      })}
                    />
                  )}
                  {isSelected && !hasFeedback && (
                    <div className={styles['no-feedback-ring']} />
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={handleNext}
            disabled={selectedImageIndex === images.length - 1}
            className={cn(
              styles['detail-carousel-button'],
              {
                [styles['detail-carousel-button--disabled']]: selectedImageIndex === images.length - 1,
              }
            )}
            aria-label="Next image"
          >
            ›
          </button>
        </div>
      </div>
    </>
  );

  // Handle image selection to show relevant feedback
  function handleImageSelect(imageIndex) {
    if (onImageIndexChange) {
      onImageIndexChange(imageIndex);
    }
  }
};

ImageCarousel.propTypes = {
  feedback: PropTypes.array.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    url: PropTypes.string.isRequired,
    page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    alt: PropTypes.string,
  })),
  onBackClick: PropTypes.func.isRequired,
  selectedImageIndex: PropTypes.number,
  onImageIndexChange: PropTypes.func,
  isLoading: PropTypes.bool,
};

ImageCarousel.defaultProps = {
  feedback: [],
  images: [],
  onBackClick: () => {},
};

export default ImageCarousel;
